<template>
  <div v-if="loading || (!loading && totalItems > 0)">
    <div class="u-text-center">
      <p> {{ totalItems }}{{ totalItems == 10000 ? '+' : ''}} <span class="u-tt-low">{{ itemName }}</span><span v-if="totalItems > 1">s</span> found</p>
    </div>
    <div
      v-if="showNavOptions"
      class="u-text-center"
    >
      <div class="pagination__controls-row">
        <div class="pagination__controls u-fontsize-sm">
          <label
            for="items-per-page"
            style="flex:0 1 auto; width: auto;"
          >
            {{ gettext(itemName + 's per page:') }}
          </label>
          <select
            id="items-per-page"
            v-model="itemsPerPage"
            class="pagination__sm-input"
          >
            <option
              v-for="val in itemsPerPageOptions"
              :key="val"
              :value="val"
            >
              {{ val }}
            </option>
          </select>
        </div>

        <div
          v-if="sortOptions !== null"
          class="pagination__controls"
        >
          <span class="u-fontsize-sm">
            Sort by:
          </span>
          <div class="btn-group">
            <button
              v-for="(key, index) in Object.keys(sortOptions)"
              :key="index"
              class="btn btn--sm"
              :class="[key === activeSort ? 'btn--dark' : 'btn--light']"
              :data-sort-preference="key"
              @click.prevent="updateSortPreference"
            >
              {{ gettext(sortOptions[key]) }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <message-search-page-nav
      :state="loading ? 'loading' : 'retrieved'"
      :initCurrentPage="currentPage"
      :totalPages="totalPages"
      @page-nav="handlePageNav"
      @select-page="updateCurrentPage"
    />
  </div>
</template>

<script>
// might refactor later on to not use this component
import MessageSearchPageNav from './message_search/MessageSearchPageNav'

export default {
  /**
   * Use this component if the page requires paginated requests.
   *
   * Capable of syncing multiple pagination components on a single page.
   * Just be sure to have only one component with 'isPrimary=true'.
   * A common case is when we show pagination at the top and bottom of a
   * list of items.
   *
   * This component handles the triggering of paginated data requests by emitting
   * the 'performSearch' event. i.e. The component containing this component
   * doesn't need to worry about triggering any data requests.
   *
   * NOTE: Currently wont work for the message search page.
   */
  name: 'PaginationComponent',
  components: {
    MessageSearchPageNav,
  },
  props: {
    loading: {
      type: Boolean,
      default: true,
    },
    itemName: {
      // the name shown for the item per page field (e.g. 'Items per page: 25')
      type: String,
      default: 'Item'
    },
    isPrimary: {
      /**
       * Used when there's multiple pagination components on the same page.
       *
       * If setting, make sure only one of the pagination components are set to 'true'.
       * The primary PaginationComponent is typically the component showing the NavOptions.
       *
       * NOTE: Not tested when more than one PaginationComponent is showing the NavOptions.
       */
      type: Boolean,
      default: true
    },
    showNavOptions: {
      // set to true if you want to display the items per page and sort options
      type: Boolean,
      default: false,
    },
    initialParams: {
      // useful if you want to override the params on page load.
      // a common use case is setting the default 'sort' param.
      type: Object,
      default: () => {
        return {
          start: 0,
          limit: 25,
          sort: '',
        }
      },
    },
    sortOptions: {
      // when set to null, the "sort by" section wont appear
      type: Object,
      default: null,
    },
    sortUpdate: {
      type: String,
      default: null
    },
    itemsPerPageOptions: {
      type: Array,
      default: () => ['5', '10', '25', '50', '100'],
    },
    totalItems: {
      type: Number,
      default: 0,
    },
    syncPaginationControls: {
      /**
       * Used when there are more than one pagination controls on the same page.
       *
       * When true it means that the pagination component needs to set it's values
       * using the URL as a reference.
       * When false we want the component to react to changes in the normal way,
       * which is to update the URL based on the component's values.
       */
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'performSearch',
    'syncPaginationControls'
  ],
  data() {
    return {
      start: 0,
      itemsPerPage: '25',
      activeSort: '',
      isPopstateUpdate: false, // useful to keep track of updates caused by a popstate
    }
  },
  computed: {
    currentPage() {
      return Math.floor(this.start / this.limit) + 1
    },
    totalPages() {
      return Math.ceil(this.totalItems / this.limit)
    },
    limit() {
      return parseInt(this.itemsPerPage)
    },
    params() {
      //  if needed, can add support for addition pagination params here
      return {
        start: this.start,
        limit: this.limit,
        sort: this.activeSort,
      }
    },
  },
  watch: {
    itemsPerPage() {
      if (!this.syncPaginationControls) {
        this.start = 0
        let newURL = this.handleURL()
        this.$nextTick(() => {
          if (this.isPopstateUpdate) {
            // we do not want to cause a pushState to occur if the update was caused
            // by a popState
            this.isPopstateUpdate = false
          } else {
            window.history.pushState(this.params, '', newURL)
          }
          this.$emit('syncPaginationControls', true)
          this.$emit('performSearch')
        })
      }
    },
    sortUpdate(newValue) {
      this.activeSort = newValue
      this.start = 0
    },
    syncPaginationControls() {
      /**
       * When there's more than one PaginationComponent on a page then there needs
       * to be a way to sync them. In order to do so we watch the 'syncPaginationControls'
       * property, which triggers each PaginationComponent to set their internal params to
       * match the query string params in the URL.
       */
      if (this.syncPaginationControls) {
        let newParams = new URLSearchParams(window.location.search)
        let newStart = newParams.get('start')
        let newLimit = newParams.get('limit')
        let newSort = newParams.get('sort')
        if (newStart !== null && this.start !== Number(newStart)) {
          this.start = Number(newStart)
        }
        if (newLimit !== null && this.itemsPerPage !== newLimit) {
          this.itemsPerPage = newLimit
        }
        if (newSort !== null && this.activeSort !== newSort) {
          this.activeSort = newSort
        }
        this.$emit('syncPaginationControls', false)
      }
    }
  },
  mounted() {
    if (this.isPrimary) {
      /**
       * Only one of the PaginationComponents on a single page need to listen to the popstate event.
       * If more than one are listening then this leads to some weird behaviour.
       * This is why it's important to have only one PaginationComponent on the page
       * have their 'isPrimary' set to true.
       */
      window.addEventListener('popstate', (e) => {
        this.handleURL(true)
        this.isPopstateUpdate = true
        this.$nextTick(() => {
          this.$emit('syncPaginationControls', true)
          this.$emit('performSearch')
        })
      })
    }

    this.handleURL(true)
    if (this.isPrimary) {
      // same reasoning as the popstate listener
      this.$nextTick(() => {
        this.$emit('syncPaginationControls', true)
        this.$emit('performSearch')
      })
    }
  },
  methods: {
    handleURL(initialLoad = false) {
      /**
       * Handles 2 main things:
       *    1. Sets the params based off the URL if 'initialLoad=True'.
       *    2. Sets the URL search params based on the component's params.
       *
       * Returns the URL with updated search params.
       */
      let url = new URL(window.location)
      if (initialLoad) {
        // on initialLoad we want to set the params from the URL
        this.start = url.searchParams.get('start') == null ? this.initialParams.start : parseInt(url.searchParams.get('start'))
        this.itemsPerPage = url.searchParams.get('limit') == null ? this.initialParams.limit : url.searchParams.get('limit')
        this.activeSort = url.searchParams.get('sort') == null ? this.initialParams.sort : url.searchParams.get('sort')
        if (!this.itemsPerPageOptions.includes(this.itemsPerPage)) {
          this.itemsPerPage = this.initialParams.limit
        }
      }

      url.searchParams.set('start', this.start)
      url.searchParams.set('limit', this.itemsPerPage)
      // sort is optional so only set it when value is present
      if (this.activeSort) {
        url.searchParams.set('sort', this.activeSort)
      }

      return url
    },
    scrollToTopControls() {
      let elBounds = document.getElementById('search-results').getBoundingClientRect()
      if (elBounds.top < 0) {
        window.scrollTo({
          top: window.pageYOffset + elBounds.top,
          left: 0,
          behavior: 'smooth'
        })
      }
    },
    handlePageNav(action) {
      let nextStart = 0
      if (action === 'prev') {
        nextStart = Math.max(this.start - this.limit, 0)
      } else if (action === 'next') {
        nextStart = Math.min(this.start + this.limit, this.totalItems)
      }

      this.scrollToTopControls()
      this.start = nextStart
      let newURL = this.handleURL()
      this.$nextTick(() => {
        window.history.pushState(this.params, '', newURL)
        this.$emit('syncPaginationControls', true)
        this.$emit('performSearch')
      })
    },
    updateCurrentPage(page) {
      let newPage = Math.max(Math.min(page, this.totalPages), 1)
      this.start = (newPage - 1) * this.limit
      let newURL = this.handleURL()
      this.$nextTick(() => {
        window.history.pushState(this.params, '', newURL)
        this.$emit('syncPaginationControls', true)
        this.$emit('performSearch')
      })
    },
    updateSortPreference(event) {
      if (event.target.dataset.sortPreference !== this.activeSort) {
        this.start = 0
        // if the previous user action was using back or forward, then this value will
        // be set to true and cause the inputs not to be updated. so we reset the value.
        this.isPopstateUpdate = false

        this.activeSort = event.target.dataset.sortPreference
        let newURL = this.handleURL()
        this.$nextTick(() => {
          window.history.pushState(this.params, '', newURL)
          this.$emit('syncPaginationControls', true)
          this.$emit('performSearch')
        })
      }
    },
  }
}
</script>

<style scoped>

</style>
